import { useEffect, useRef, useState } from 'react';
import { getClientEnv } from '../utils/env-var-resolver';

const FALLBACK_VIEWPORT_HEIGHT_FLOOR = 1000;

/**
 * If the parent shell doesn't reply with a `viewport-height` message
 * within this window, callers fall back to local viewport signals and
 * mark the handshake "done" anyway so the UI isn't blocked forever.
 */
const PARENT_HANDSHAKE_TIMEOUT_MS = 250;

/**
 * Outbound message from the iframe to the host shell. `open: true` asks
 * the host to enter overlay mode: scroll the iframe element to the top
 * of the visible viewport, shrink the iframe to exactly the visible
 * viewport height, lock parent-page scroll, and ignore the iframe's
 * own auto-resize `resize` messages until the matching `open: false`
 * arrives. The host replies with a `viewport-height` message carrying
 * the resolved height.
 */
interface IOverlayModeMessage {
	type: 'overlay-mode';
	open: boolean;
}

interface IViewportHeightMessage {
	type: 'viewport-height';
	height: number;
}

function isTrustedMessageOrigin(origin: string): boolean {
	const allowedOrigin = getClientEnv('PARENT_ORIGIN');
	if (allowedOrigin) return origin === allowedOrigin;
	if (typeof window === 'undefined') return false;
	if (origin === window.location.origin) return true;

	// In embedded mode without an explicit allowlist, accept the embedding
	// page's origin as a safe fallback so cross-origin host shells can
	// complete the overlay-mode viewport-height handshake.
	if (
		window.parent !== window &&
		typeof document !== 'undefined' &&
		document.referrer
	) {
		try {
			return origin === new URL(document.referrer).origin;
		} catch {
			return false;
		}
	}

	return false;
}

function isViewportHeightMessage(
	data: unknown
): data is IViewportHeightMessage {
	if (typeof data !== 'object' || data === null) return false;
	if (!('type' in data) || !('height' in data)) return false;
	return (
		data.type === 'viewport-height' &&
		typeof data.height === 'number' &&
		Number.isFinite(data.height) &&
		data.height > 0
	);
}

function getLocalFallbackViewportHeight(): number {
	if (typeof window === 'undefined') return FALLBACK_VIEWPORT_HEIGHT_FLOOR;
	const screenHeight =
		typeof window.screen === 'undefined' ? 0 : window.screen.availHeight;
	const innerHeight = window.innerHeight || 0;
	// Inside an iframe `innerHeight` measures the iframe element (often
	// grown by the host to fit body content), NOT the user's browser
	// viewport — so taking the larger of the two would make the drawer
	// taller than the screen. Clamp to `screen.availHeight` instead.
	// It's a coarse upper bound (doesn't subtract browser chrome) but
	// it's the best a cross-origin iframe can read without help from
	// the host shell.
	if (window.parent !== window) {
		const candidates = [screenHeight, innerHeight].filter((value) => value > 0);
		if (candidates.length === 2) return Math.min(...candidates);
		if (candidates.length === 1) return candidates[0]!;
		return FALLBACK_VIEWPORT_HEIGHT_FLOOR;
	}
	return innerHeight || FALLBACK_VIEWPORT_HEIGHT_FLOOR;
}

interface IReportedViewport {
	height: number | null;
	/**
	 * `true` once the host has either acknowledged overlay mode (via
	 * `viewport-height`) or the handshake has timed out. Callers use this
	 * to defer first paint of overlay UI until the iframe has been pinned
	 * inside the parent's visible viewport — otherwise the panel paints
	 * once at the iframe's natural inline position (which may be partly
	 * above the parent viewport when scrolled) before the parent moves the
	 * iframe, causing a visible cut-off flash.
	 */
	ready: boolean;
}

/**
 * Resolve the parent's visible viewport height while `isActive` is true.
 *
 * Preferred path: host responds to our `overlay-mode` request with a
 * `{type: 'viewport-height', height}` message carrying the exact pixel
 * height it shrank the iframe to. We use that as the authoritative value.
 *
 * Degraded fallback (older host, no overlay-mode handler): no reply
 * arrives, so we fall back to whatever local signals exist —
 * `window.parent.innerHeight` for same-origin embeds, otherwise
 * `window.screen.availHeight` / `window.innerHeight`. The drawer is still
 * not screen-pinned (iframe `position: fixed` is iframe-local), but the
 * panel sizes sensibly so the regression doesn't get worse than the
 * pre-overlay-mode behaviour.
 */
function useReportedViewportHeight(isActive: boolean): IReportedViewport {
	const [reportedHeight, setReportedHeight] = useState<number | null>(null);
	const [fallbackHeight, setFallbackHeight] = useState<number>(0);
	const [timedOut, setTimedOut] = useState<boolean>(false);
	const canReadParentHeightRef = useRef(true);

	useEffect(() => {
		if (!isActive) return;

		function refreshFallback() {
			if (typeof window === 'undefined') {
				setFallbackHeight(FALLBACK_VIEWPORT_HEIGHT_FLOOR);
				return;
			}
			if (window.parent === window) {
				setFallbackHeight(window.innerHeight || FALLBACK_VIEWPORT_HEIGHT_FLOOR);
				return;
			}
			if (canReadParentHeightRef.current) {
				try {
					const parentInnerHeight = window.parent.innerHeight;
					if (Number.isFinite(parentInnerHeight) && parentInnerHeight > 0) {
						setFallbackHeight(parentInnerHeight);
						return;
					}
				} catch {
					canReadParentHeightRef.current = false;
				}
			}
			setFallbackHeight(getLocalFallbackViewportHeight());
		}

		// Tracks the rAF id of an in-flight resize-confirmation poll so we
		// can cancel it on cleanup or when a fresher message arrives.
		let resizeConfirmFrame: number | null = null;

		function handleMessage(event: MessageEvent<unknown>) {
			if (!isTrustedMessageOrigin(event.origin)) return;
			if (event.source !== window.parent) return;
			if (!isViewportHeightMessage(event.data)) return;

			// The parent posts `viewport-height` from a `requestAnimationFrame`
			// callback *before* its own paint applies the iframe's new
			// `position: fixed; top: 0; height: 100vh` styles. If we mark
			// the handshake ready immediately, the drawer's first paint
			// happens while the iframe is still at its natural inline
			// position (tall, scrolled past viewport top) — and the panel
			// briefly anchors to that wrong position, clipping its top.
			//
			// Wait until the iframe's *own* `innerHeight` approaches the
			// reported value before reporting ready, which means the
			// parent has actually completed the resize and the iframe is
			// pinned where its `position: fixed` descendants expect.
			// Cap the poll so a parent that never resizes (or reports a
			// stale height) still unblocks the UI.
			const targetHeight = event.data.height;
			if (resizeConfirmFrame !== null) {
				cancelAnimationFrame(resizeConfirmFrame);
			}
			let attempts = 0;
			const checkResized = () => {
				const closeEnough = Math.abs(window.innerHeight - targetHeight) < 8;
				if (closeEnough || attempts >= 12) {
					resizeConfirmFrame = null;
					setReportedHeight(targetHeight);
					return;
				}
				attempts += 1;
				resizeConfirmFrame = requestAnimationFrame(checkResized);
			};
			checkResized();
		}

		refreshFallback();
		window.addEventListener('message', handleMessage);
		window.addEventListener('resize', refreshFallback);
		const refreshInterval = setInterval(refreshFallback, 1000);
		const handshakeTimeout = window.setTimeout(
			() => setTimedOut(true),
			PARENT_HANDSHAKE_TIMEOUT_MS
		);

		return () => {
			window.removeEventListener('message', handleMessage);
			window.removeEventListener('resize', refreshFallback);
			clearInterval(refreshInterval);
			window.clearTimeout(handshakeTimeout);
			if (resizeConfirmFrame !== null) {
				cancelAnimationFrame(resizeConfirmFrame);
			}
			setReportedHeight(null);
			setTimedOut(false);
		};
	}, [isActive]);

	const height = reportedHeight ?? (fallbackHeight > 0 ? fallbackHeight : null);
	const ready = reportedHeight !== null || timedOut;
	return { height, ready };
}

/**
 * Drives the iframe's overlay-mode handshake with the host shell so a
 * `position: fixed` panel inside the iframe behaves like a true
 * viewport-anchored overlay rather than being trapped at the iframe's
 * intrinsic top edge.
 *
 * Protocol (see `IOverlayModeMessage` above):
 *  1. On activate, post `{type:'overlay-mode', open:true}`.
 *  2. Host (e.g. lqa-app `MicroApp.tsx`) scrolls the parent so the
 *     iframe sits at viewport top, shrinks the iframe to visible
 *     viewport height, locks parent-page scroll, and stops honouring
 *     iframe-driven `resize` messages until the matching close.
 *  3. Host replies with `{type:'viewport-height', height}`; we hand
 *     that back to the caller for the panel's `height` style.
 *  4. On deactivate (effect cleanup), post `{type:'overlay-mode',
 *     open:false}` so the host restores body overflow, scroll position,
 *     and the iframe's auto-resize behaviour.
 *
 * Hosts that don't implement `overlay-mode` simply ignore the message;
 * we fall back to local viewport signals so the caller still gets a
 * usable (if not perfectly screen-anchored) height.
 */
export interface IIframeFillResult {
	/** Resolved viewport height in px (reported by parent or local fallback). */
	viewportHeight: number | null;
	/**
	 * `true` once the parent has acknowledged overlay mode (via the
	 * `viewport-height` reply) or the handshake has timed out. Callers
	 * should defer painting overlay UI until this flips true so the panel
	 * doesn't briefly appear at the iframe's natural inline position
	 * (cut off by the parent's scroll) before the parent moves the iframe
	 * into the visible viewport.
	 */
	ready: boolean;
}

export function useIframeFill(isActive: boolean): IIframeFillResult {
	const { height: viewportHeight, ready } = useReportedViewportHeight(isActive);

	useEffect(() => {
		if (!isActive) return;
		if (typeof window === 'undefined') return;
		if (window.parent === window) return;

		const targetOrigin = getClientEnv('PARENT_ORIGIN') || '*';
		try {
			// Parent owns the scroll lifecycle now — it captures the
			// pre-overlay `scrollY`, scrolls to top before measuring the
			// iframe container (so the drawer's top edge isn't cut off
			// when the user had scrolled past), and restores the saved
			// `scrollY` on the matching `overlay-mode: false`.
			window.parent.postMessage(
				{ type: 'overlay-mode', open: true } satisfies IOverlayModeMessage,
				targetOrigin
			);
		} catch {
			// best-effort — cross-origin parents that strictly enforce
			// origin will swallow the post, and the fallback height in
			// `useReportedViewportHeight` still produces a usable value.
		}

		return () => {
			try {
				window.parent.postMessage(
					{ type: 'overlay-mode', open: false } satisfies IOverlayModeMessage,
					targetOrigin
				);
			} catch {
				// best-effort
			}
		};
	}, [isActive]);

	// When the parent shell isn't embedding us (standalone dev / tests),
	// there's no overlay handshake to wait for — report ready immediately
	// so callers don't sit on a hidden panel forever.
	const isEmbedded = typeof window !== 'undefined' && window.parent !== window;
	return { viewportHeight, ready: isEmbedded ? ready : true };
}
